import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import { usePageView } from '../../hooks/usePageView';
import './Downloads.css';

function formatSize(bytes) {
  if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return Math.round(bytes / 1024) + ' KB';
}

function PreviewModal({ file, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/api/quotations/${encodeURIComponent(file.filename)}/preview`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError('加载失败，请重试'))
      .finally(() => setLoading(false));
  }, [file.filename]);

  // ESC 关闭
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const headers = data?.rows?.[0] ?? [];
  const rows = data?.rows?.slice(1) ?? [];

  return (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="preview-header">
          <span className="preview-title">
            <span className="excel-badge">X</span>
            {file.originalName}
          </span>
          <button className="preview-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="preview-body">
          {loading && <p className="preview-tip">加载中…</p>}
          {error && <p className="preview-tip preview-error">{error}</p>}
          {data && (
            <div className="preview-table-wrap">
              <table className="preview-table">
                <thead>
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i}>{h !== '' ? String(h) : `列${i + 1}`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri}>
                      {headers.map((_, ci) => (
                        <td key={ci}>
                          {row[ci] !== undefined && row[ci] !== ''
                            ? String(row[ci])
                            : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileCard({ file, c, onPreview }) {
  const downloadUrl = `/api/quotations/download/${encodeURIComponent(file.filename)}`;

  return (
    <div className="file-card">
      <div className="excel-icon">
        <span className="excel-x">X</span>
      </div>
      <div className="file-info">
        <p className="file-name">{file.originalName}</p>
        <p className="file-meta">
          <span>{formatSize(file.size)}</span>
          <span className="file-dot">·</span>
          <span>
            {c.updatedAt}{' '}
            {new Date(file.uploadedAt).toLocaleDateString('zh-CN')}
          </span>
        </p>
      </div>
      <div className="file-actions">
        <button
          className="btn-icon btn-preview"
          onClick={() => onPreview(file)}
          title={c.preview}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        <button
          className="btn-icon btn-download"
          onClick={() => window.open(downloadUrl, '_blank')}
          title={c.download}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function Downloads() {
  usePageView('downloads');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const { t } = useLanguage();
  const c = t.downloads;

  useEffect(() => {
    fetch('/api/quotations')
      .then((r) => r.json())
      .then((data) => setFiles(Array.isArray(data) ? data : []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, []);

  const closePreview = useCallback(() => setPreviewFile(null), []);

  return (
    <div className="downloads-page">
      <Helmet>
        <title>资料下载 - 首嘉/深圳瀚阳</title>
        <meta name="description" content="下载首嘉/深圳瀚阳报价单及相关资料文件。" />
        <meta property="og:title" content="资料下载 - 首嘉/深圳瀚阳" />
        <meta property="og:description" content="下载首嘉/深圳瀚阳报价单及相关资料文件。" />
        <meta property="og:url" content="https://www.sz-showjoy.com/downloads" />
        <meta name="keywords" content="物流报价单下载,首嘉/深圳瀚阳资料,货运报价,物流文件下载" />
        <link rel="canonical" href="https://www.sz-showjoy.com/downloads" />
      </Helmet>
      <div className="page-header">
        <div className="container">
          <h1>{c.pageTitle}</h1>
          <p>{c.pageSubtitle}</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading ? (
            <p className="downloads-tip">{c.loading}</p>
          ) : files.length === 0 ? (
            <p className="downloads-tip">{c.noFiles}</p>
          ) : (
            <div className="files-list">
              {files.map((file) => (
                <FileCard
                  key={file.filename}
                  file={file}
                  c={c}
                  onPreview={setPreviewFile}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {previewFile && (
        <PreviewModal file={previewFile} onClose={closePreview} />
      )}
    </div>
  );
}
